import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import { useOcAdminStore } from "@/store/oc-admin-store";
import { ocAdminClearSessionToken } from "@/lib/oc-admin-api";
import { useOcTheme } from "@/hooks/useOcTheme";
import {
  ocAdminTenantsApi,
  ocAdminLeadsApi,
  ocAdminContractsApi,
  ocAdminCampaignsApi,
  ocAdminUsersApi,
  ocAdminFetch,
} from "@/lib/oc-admin-api";

// Map of route path → prefetch config used on nav hover
const OC_PREFETCH_MAP: Record<string, { queryKey: unknown[]; queryFn: () => Promise<unknown> }> = {
  "/tenants":              { queryKey: ["admin", "tenants"],                   queryFn: ocAdminTenantsApi.list },
  "/system-health":        { queryKey: ["admin", "health"],                    queryFn: () => ocAdminFetch("/health") },
  "/leads":                { queryKey: ["admin", "leads"],                     queryFn: ocAdminLeadsApi.list },
  "/contracts":            { queryKey: ["admin", "contracts"],                 queryFn: ocAdminContractsApi.list },
  "/campaigns":            { queryKey: ["admin", "campaigns"],                 queryFn: ocAdminCampaignsApi.list },
  "/users":                { queryKey: ["admin", "internal-users"],            queryFn: ocAdminUsersApi.list },
};

function OcShieldMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 54" fill="none" className={className} aria-hidden="true">
      <path d="M24 2 L44 10 L44 26 C44 37 34 45 24 50 C14 45 4 37 4 26 L4 10 Z"
            fill="none" stroke="#0ea5e9" strokeWidth="2.5"/>
      <path d="M13 26 L20 33 L35 18" stroke="#0ea5e9" strokeWidth="3.5"
            strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function OcAdminThemeToggle() {
  const { isDark, toggleTheme } = useOcTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-oc-navy-700 transition-colors"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
        </svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Nav item definitions
// ---------------------------------------------------------------------------

interface OcNavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: { label: string; path: string; soon?: boolean }[];
}

function IconGrid() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}
function IconActivity() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.5l3.75-3.75 3 3L13.5 9l3 3L21 7.5" />
    </svg>
  );
}
function IconFunnel() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
    </svg>
  );
}
function IconDocument() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}
function IconCurrencyDollar() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function IconStar() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
  );
}
function IconMegaphone() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}
function IconChevronDown() {
  return (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}
function IconSupport() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.712 4.33a9.027 9.027 0 0 1 1.652 1.306c.51.51.944 1.064 1.306 1.652M16.712 4.33l-3.448 4.138m3.448-4.138a9.014 9.014 0 0 0-9.424 0M19.67 7.288l-4.138 3.448m4.138-3.448a9.014 9.014 0 0 1 0 9.424m-4.138-5.976a3.736 3.736 0 0 0-.88-1.388 3.737 3.737 0 0 0-1.388-.88m2.268 2.268a3.765 3.765 0 0 1 0 2.528m-2.268-4.796a3.765 3.765 0 0 0-2.528 0m4.796 4.796c-.181.506-.475.982-.88 1.388a3.736 3.736 0 0 1-1.388.88m2.268-2.268 4.138 3.448m0 0a9.027 9.027 0 0 1-1.306 1.652c-.51.51-1.064.944-1.652 1.306m0 0-3.448-4.138m3.448 4.138a9.015 9.015 0 0 1-9.424 0m5.976-4.138a3.765 3.765 0 0 1-2.528 0m0 0a3.736 3.736 0 0 1-1.388-.88 3.737 3.737 0 0 1-.88-1.388m2.268 2.268L7.288 19.67m0 0a9.024 9.024 0 0 1-1.652-1.306 9.027 9.027 0 0 1-1.306-1.652m0 0 4.138-3.448M4.33 16.712a9.014 9.014 0 0 1 0-9.424m4.138 5.976a3.765 3.765 0 0 1 0-2.528m0 0c.181-.506.475-.982.88-1.388a3.736 3.736 0 0 1 1.388-.88m-2.268 2.268L4.33 7.288m6.406 1.18L7.288 4.33m0 0a9.024 9.024 0 0 1 1.652-1.306A9.025 9.025 0 0 1 10.59 2.1" />
    </svg>
  );
}

const NAV_ITEMS: OcNavItem[] = [
  { label: "Tenants",       path: "/tenants",       icon: <IconGrid /> },
  { label: "Leads Pipeline",path: "/leads",          icon: <IconFunnel /> },
  { label: "Billing",       path: "/billing",        icon: <IconCurrencyDollar /> },
  { label: "Campaigns",     path: "/campaigns",      icon: <IconMegaphone /> },
  { label: "Support",       path: "/support",        icon: <IconSupport /> },
  {
    label: "Resources",
    path: "/resources",
    icon: <IconDocument />,
    children: [
      { label: "Pricing Table",      path: "/resources/pricing" },
      { label: "HECVAT Prep",        path: "/resources/hecvat" },
      { label: "Workflow Templates", path: "/resources/workflows" },
      { label: "Technical Docs",     path: "/resources/tech-docs" },
      { label: "User Manual",        path: "/resources/user-manual" },
      { label: "Agent Setup UI",     path: "/resources/agent-ui" },
      { label: "Business Cards",     path: "/resources/business-cards" },
      { label: "Hybrid AD Lab",      path: "/resources/hybrid-lab" },
      { label: "Staging Environment", path: "/resources/staging" },
    ],
  },
  { label: "Internal Users", path: "/users",         icon: <IconUsers /> },
  { label: "System Health", path: "/system-health",  icon: <IconActivity /> },
];

// ---------------------------------------------------------------------------
// Role badge
// ---------------------------------------------------------------------------

const ROLE_BADGE_STYLES: Record<string, string> = {
  owner: "bg-oc-teal-500/20 text-oc-teal-400 ring-1 ring-oc-teal-500/40",
  admin: "bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40",
  viewer: "bg-slate-600/40 text-slate-400 ring-1 ring-slate-500/40",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface OcAdminLayoutProps {
  children: React.ReactNode;
}

export default function OcAdminLayout({ children }: OcAdminLayoutProps) {
  const internalUser = useOcAdminStore((s) => s.internalUser);
  const clearSession = useOcAdminStore((s) => s.clearSession);
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const qc = useQueryClient();

  function isGroupActive(path: string) {
    return location.pathname.startsWith(path);
  }

  function isGroupExpanded(path: string) {
    return expandedGroups[path] ?? isGroupActive(path);
  }

  function toggleGroup(path: string) {
    setExpandedGroups((prev) => ({ ...prev, [path]: !isGroupExpanded(path) }));
  }

  const handleNavClick = () => setMobileOpen(false);

  function handleSignOut() {
    ocAdminClearSessionToken();
    clearSession();
    navigate("/login", { replace: true });
  }

  function handlePrefetch(path: string) {
    const cfg = OC_PREFETCH_MAP[path];
    if (cfg) {
      void qc.prefetchQuery({
        queryKey: cfg.queryKey,
        queryFn: cfg.queryFn,
        staleTime: 2 * 60_000,
      });
    }
  }

  return (
    <div className="flex h-screen bg-white dark:bg-oc-navy-900 text-slate-800 dark:text-slate-100 print:block print:h-auto">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Sidebar                                                              */}
      {/* ------------------------------------------------------------------ */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-56 flex flex-col flex-none",
          "border-r border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-oc-navy-950",
          "transition-transform duration-200",
          "lg:static lg:z-auto lg:translate-x-0",
          "print:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Logo */}
        <div className="h-12 flex items-center justify-between gap-2 px-4 border-b border-slate-200 dark:border-slate-700/50">
          <div className="flex items-center gap-2">
            <OcShieldMark className="w-7 h-7 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-tight">
                OnboardConnect
              </span>
              <span className="text-[10px] font-medium text-oc-teal-500 leading-tight">
                For Slate CRM
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            aria-label="Close navigation"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
          {NAV_ITEMS.map((item) => {
            if (item.children) {
              const isActive = isGroupActive(item.path);
              const isExpanded = isGroupExpanded(item.path);

              return (
                <div key={item.path}>
                  <button
                    onClick={() => toggleGroup(item.path)}
                    className={clsx(
                      "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors",
                      isActive
                        ? "bg-slate-200 dark:bg-oc-navy-700 text-slate-900 dark:text-slate-100"
                        : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-oc-navy-700/60",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className={isActive ? "text-oc-teal-600 dark:text-oc-teal-500" : "text-slate-400"}>
                        {item.icon}
                      </span>
                      {item.label}
                    </span>
                    <span
                      className={clsx(
                        "transition-transform",
                        isExpanded ? "rotate-180" : "",
                      )}
                    >
                      <IconChevronDown />
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="ml-6 mt-0.5 space-y-0.5">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={handleNavClick}
                          onMouseEnter={() => handlePrefetch(child.path)}
                          className={({ isActive }) =>
                            clsx(
                              "flex items-center justify-between gap-1 px-3 py-1.5 rounded-md text-xs transition-colors",
                              isActive
                                ? "bg-oc-teal-500/15 text-oc-teal-600 dark:text-oc-teal-400 font-medium"
                                : "text-slate-500 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-oc-navy-700/50",
                            )
                          }
                        >
                          <span>{child.label}</span>
                          {child.soon && (
                            <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30">
                              Soon
                            </span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                onMouseEnter={() => handlePrefetch(item.path)}
                className={({ isActive }) =>
                  clsx(
                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors",
                    isActive
                      ? "bg-slate-200 dark:bg-oc-navy-700 text-slate-900 dark:text-slate-100 [&>span:first-child]:text-oc-teal-600 dark:[&>span:first-child]:text-oc-teal-500"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-oc-navy-700/60",
                  )
                }
              >
                <span className="text-slate-400">{item.icon}</span>
                {item.label}
              </NavLink>
            );
          })}
        </nav>

      </aside>

      {/* ------------------------------------------------------------------ */}
      {/* Main area                                                            */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 print:overflow-visible">
        {/* Header */}
        <header className="h-12 flex-none flex items-center justify-between px-5 border-b border-slate-200 dark:border-slate-700/50 bg-white/80 dark:bg-oc-navy-900/80 backdrop-blur-sm print:hidden">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-1.5 rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-oc-navy-700 transition-colors mr-2"
              aria-label="Open navigation"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
            <div className="text-xs font-mono text-slate-400 dark:text-slate-500 uppercase tracking-widest">
              OnboardConnect Super-Admin
            </div>
          </div>
          <div className="flex items-center gap-3">
            <OcAdminThemeToggle />
            {internalUser && (
              <>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{internalUser.name}</span>
                <span
                  className={clsx(
                    "text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider",
                    ROLE_BADGE_STYLES[internalUser.role] ?? ROLE_BADGE_STYLES.viewer,
                  )}
                >
                  {internalUser.role}
                </span>
                <button
                  type="button"
                  onClick={handleSignOut}
                  title="Sign out"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                  </svg>
                  Sign out
                </button>
              </>
            )}
          </div>
        </header>

        {/* Page content. The layout owns the outer 24px gutter; individual
            pages must NOT also wrap themselves in `p-6` or the padding doubles
            (48px). Pages that need extra spacing should use space-y-* on their
            root, not additional padding. */}
        <main className="flex-1 flex flex-col overflow-y-auto print:overflow-visible">
          <div className="flex-1 p-6 print:p-0">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
